import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { downloadPdf } from "@/lib/files";
import { fmtDA } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { FileDown, RefreshCw, Loader2 } from "lucide-react";

const MOIS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const SEL = "h-9 rounded-md border bg-background px-2 text-sm";
const COLS = [["ca_ht", "CA HT"], ["tva_collectee", "TVA collectée"], ["tva_deductible", "TVA déductible"], ["tva_a_payer", "TVA à payer"],
  ["credit_reporter", "Précompte à reporter"], ["timbre", "Timbre"], ["irg_salaires", "IRG salaires"], ["total_a_payer", "Total à payer"]];
const slug = (s) => (s || "").replace(/\s+/g, "-");

const Row = ({ e, admin, onRetablir, busy }) => (
  <tr data-testid={`g50e-row-${e.id}`}>
    <td className="py-2 pr-2 font-medium whitespace-nowrap">{MOIS[e.mois - 1]} {e.annee}</td>
    <td className="py-2 pr-2 text-xs">{e.societe_nom}</td>
    {COLS.map(([k]) => <td key={k} className={`py-2 pr-2 tabular text-right ${k === "total_a_payer" ? "font-bold" : ""}`}>{fmtDA(e.data[k])}</td>)}
    <td className="py-2 pr-2 text-xs text-muted-foreground whitespace-nowrap">{new Date(e.etabli_le).toLocaleDateString("fr-FR")}<br />{e.etabli_par}</td>
    <td className="py-2 text-right whitespace-nowrap">
      <Button size="sm" variant="outline" className="gap-1" onClick={() => downloadPdf(`/g50-etablis/${e.societe_id}/${e.annee}/${e.mois}/pdf`, `G50-${slug(e.societe_nom)}-${e.annee}-${String(e.mois).padStart(2, "0")}`)} data-testid={`g50e-pdf-${e.id}`}><FileDown className="h-4 w-4" /> PDF</Button>
      {admin && <Button size="sm" variant="ghost" className="gap-1 ml-1" disabled={busy} onClick={() => onRetablir(e)} data-testid={`g50e-retablir-${e.id}`}><RefreshCw className="h-4 w-4" /> Rétablir</Button>}
    </td>
  </tr>
);

export default function G50Etablis() {
  const { societeId } = useApp();
  const { role } = useAuth();
  const [annee, setAnnee] = useState(new Date().getFullYear());
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setItems(null);
    api.get("/g50-etablis", { params: { annee, ...(societeId !== "all" && { societe_id: societeId }) } }).then((r) => setItems(r.data)).catch(() => setItems([]));
  };
  useEffect(() => { load(); }, [annee, societeId]); // eslint-disable-line

  const onRetablir = async (e) => {
    if (!window.confirm(`Rétablir le G50 de ${MOIS[e.mois - 1]} ${e.annee} (${e.societe_nom}) avec les factures actuelles ?`)) return;
    setBusy(true);
    try {
      await api.post(`/g50-etablis/${e.societe_id}/${e.annee}/${e.mois}/retablir`);
      toast.success("G50 rétabli");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const onAnnee = async () => {
    if (!window.confirm(`Établir tous les G50 ${annee} (mois clos) avec les factures actuelles ? Les G50 déjà établis seront recalculés.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/g50-etablis/annee/${annee}`, null, { params: { societe_id: societeId } });
      toast.success(`${data.etablis} G50 établis pour ${annee}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="g50-etablis-page">
      <PageHeader title="Préparer G50" subtitle="Établis automatiquement le 1er de chaque mois à partir des factures de vente et d'achat — figés à leur date d'établissement" />
      <div className="rounded-xl border bg-card p-5">
        <div className="flex items-center gap-3 mb-4">
          <select className={SEL} value={annee} onChange={(e) => setAnnee(Number(e.target.value))} data-testid="g50e-annee">
            {[2025, 2026].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <p className="text-xs text-muted-foreground flex-1">Formulaire Série G N°50 (IRG/TAP, récapitulation & timbre, TVA). Seul l'administrateur peut rétablir un G50.</p>
          {role === "administrateur" && <Button className="gap-1" disabled={busy} onClick={onAnnee} data-testid="g50e-etablir-annee">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Établir tous les G50 {annee}</Button>}
        </div>
        {items === null ? <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
          : items.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center" data-testid="g50e-empty">Aucun G50 établi pour cette période.</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[1300px]">
                <thead><tr className="text-left text-muted-foreground border-b">
                  <th className="py-2 pr-2 font-medium">Mois</th><th className="py-2 pr-2 font-medium">Société</th>
                  {COLS.map(([k, h]) => <th key={k} className="py-2 pr-2 font-medium text-right">{h}</th>)}
                  <th className="py-2 pr-2 font-medium">Établi le</th><th />
                </tr></thead>
                <tbody className="divide-y">{items.map((e) => <Row key={e.id} e={e} admin={role === "administrateur"} onRetablir={onRetablir} busy={busy} />)}</tbody>
              </table>
            </div>
          )}
      </div>
    </div>
  );
}
